import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import {
  ACCESS_AVAILABILITY_MAP,
  ACCESS_FREQUENCY_MAP,
  ACCESS_HOURS_MAP,
  ACCESS_PERMISSION_MAP,
  FEATURES_MAP
} from 'src/constants'
import {
  ACCESS_AVAILABILITY_LABELS_EN,
  ACCESS_FREQUENCY_LABELS_EN,
  ACCESS_HOURS_LABELS_EN,
  ACCESS_PERMISSION_LABELS_EN,
  FEATURE_LABELS_EN
} from 'src/constants/spaceLabelsEn'
import { Features } from 'src/types/graphql'

/**
 * Locale-aware amenity + access label maps.
 *
 * Returns objects with the SAME shape as the `FEATURES_MAP` / `ACCESS_*_MAP`
 * constants, so consumers keep their existing `MAP[value]` lookups and only
 * swap where the map comes from. In `es` these ARE the original constants
 * (same object identity) — zero behavior change. In `en` the labels are
 * replaced while icons / `deprecated` / descriptions are preserved from the
 * canonical map.
 *
 * Use this instead of importing `FEATURES_MAP` / `ACCESS_*_MAP` directly in any
 * component that RENDERS a label. Importing the raw constant is still correct
 * when you only need `icon` or `deprecated`.
 */
export default function useSpaceLabels() {
  const { i18n } = useTranslation()
  const isEnglish = i18n.language === 'en'

  return useMemo(() => {
    if (!isEnglish) {
      return {
        FEATURES_MAP,
        ACCESS_PERMISSION_MAP,
        ACCESS_FREQUENCY_MAP,
        ACCESS_HOURS_MAP,
        ACCESS_AVAILABILITY_MAP
      }
    }

    const featuresEn = Object.fromEntries(
      Object.entries(FEATURES_MAP).map(([key, value]) => [
        key,
        { ...value, label: FEATURE_LABELS_EN[key as Features] ?? value.label }
      ])
    ) as typeof FEATURES_MAP

    return {
      FEATURES_MAP: featuresEn,
      ACCESS_PERMISSION_MAP: ACCESS_PERMISSION_LABELS_EN,
      ACCESS_FREQUENCY_MAP: ACCESS_FREQUENCY_LABELS_EN,
      ACCESS_HOURS_MAP: ACCESS_HOURS_LABELS_EN,
      ACCESS_AVAILABILITY_MAP: ACCESS_AVAILABILITY_LABELS_EN
    }
  }, [isEnglish])
}
